/* ============================================================================
   THE LEADERSHIP IMPRINT – TEAM IMPRINT
   The corporate hero artifact: the whole team plotted on the quadrant, style
   balance, where they're heavy or missing (with the real consequence), the
   friction to watch, and the one move. Built from real cohort results.

   render(cohort, meta) -> inner HTML for #ts
     cohort = { company, seats, sessions: [ { name, style, score } ] }
     (only completed sessions with a score are used)
   ========================================================================== */
(function (root) {
  'use strict';
  var C = (root.TLI && root.TLI.content) || (typeof require === 'function' ? require('./tli-content.js') : null);
  var LABEL = { TRACTOR: 'Tractor', BUS: 'Bus', JET: 'Jet', ROCKET: 'Rocket' };
  var HEX = { TRACTOR: '#C87F0A', BUS: '#2E8B45', JET: '#1B3F8B', ROCKET: '#C41E1E' };
  var TINT = { TRACTOR: '#F4EEE2', BUS: '#E8F0EA', JET: '#E9ECF4', ROCKET: '#F6EAEA' };
  var ORDER = ['TRACTOR', 'BUS', 'JET', 'ROCKET'];
  var POS = { TRACTOR: 'Measured · Task', BUS: 'Measured · People', JET: 'Fast · People', ROCKET: 'Fast · Task' };

  function esc(s) { return String(s == null ? '' : s).replace(/[&<>]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]; }); }
  function paras(a) { return (a || []).map(function (p) { return '<p class="ts-body">' + p + '</p>'; }).join(''); }

  /* multi-point quadrant: every leader as a numbered dot, colored by style */
  function teamGrid(people) {
    var W = 620, M = 60, P = W - M * 2, H = W;
    function X(p) { return M + (p / 100) * P; }
    function Y(p) { return M + P - (p / 100) * P; }
    function CX(p) { return Math.max(M + 10, Math.min(M + P - 10, X(p))); }
    function CY(p) { return Math.max(M + 10, Math.min(M + P - 10, Y(p))); }
    var cx = M + P / 2, cy = M + P / 2, half = P / 2;
    var g = '<svg viewBox="0 0 ' + W + ' ' + H + '" width="100%" font-family="Inter,system-ui,sans-serif">';
    g += '<rect x="' + M + '" y="' + M + '" width="' + half + '" height="' + half + '" fill="' + TINT.BUS + '"/>';
    g += '<rect x="' + cx + '" y="' + M + '" width="' + half + '" height="' + half + '" fill="' + TINT.JET + '"/>';
    g += '<rect x="' + M + '" y="' + cy + '" width="' + half + '" height="' + half + '" fill="' + TINT.TRACTOR + '"/>';
    g += '<rect x="' + cx + '" y="' + cy + '" width="' + half + '" height="' + half + '" fill="' + TINT.ROCKET + '"/>';
    g += '<rect x="' + M + '" y="' + M + '" width="' + P + '" height="' + P + '" fill="none" stroke="#4A5560" stroke-width="1.5"/>';
    g += '<line x1="' + cx + '" y1="' + M + '" x2="' + cx + '" y2="' + (M + P) + '" stroke="#9AA3AC" stroke-width="0.75" stroke-dasharray="4 3"/>';
    g += '<line x1="' + M + '" y1="' + cy + '" x2="' + (M + P) + '" y2="' + cy + '" stroke="#9AA3AC" stroke-width="0.75" stroke-dasharray="4 3"/>';
    function corner(x, y, anchor, s) { return '<text x="' + x + '" y="' + y + '" text-anchor="' + anchor + '" fill="' + HEX[s] + '" font-size="13" font-weight="700">' + LABEL[s] + '</text>'; }
    g += corner(M + 10, M + 20, 'start', 'BUS') + corner(M + P - 10, M + 20, 'end', 'JET');
    g += corner(M + 10, M + P - 10, 'start', 'TRACTOR') + corner(M + P - 10, M + P - 10, 'end', 'ROCKET');
    g += '<text x="' + cx + '" y="' + (M + P + 26) + '" text-anchor="middle" fill="#8A939C" font-size="10" letter-spacing="1.5">MEASURED  ·  FAST</text>';
    g += '<text x="' + (M - 14) + '" y="' + cy + '" text-anchor="middle" fill="#8A939C" font-size="10" letter-spacing="1.5" transform="rotate(-90 ' + (M - 14) + ' ' + cy + ')">TASK  ·  PEOPLE</text>';
    // simple overlap jitter: nudge dots that share a spot
    var placed = [];
    people.forEach(function (pp, i) {
      var px = CX(pp.pace), py = CY(pp.priority), tries = 0;
      while (placed.some(function (q) { return Math.abs(q.x - px) < 15 && Math.abs(q.y - py) < 15; }) && tries < 12) {
        var ang = tries * 1.9; px = CX(pp.pace) + Math.cos(ang) * (10 + tries); py = CY(pp.priority) + Math.sin(ang) * (10 + tries); tries++;
      }
      placed.push({ x: px, y: py });
      g += '<circle cx="' + px + '" cy="' + py + '" r="12" fill="' + HEX[pp.style] + '" fill-opacity="0.92" stroke="#fff" stroke-width="1.5"/>';
      g += '<text x="' + px + '" y="' + (py + 4) + '" text-anchor="middle" fill="#fff" font-size="11" font-weight="700">' + (i + 1) + '</text>';
    });
    return g + '</svg>';
  }

  function render(cohort, meta) {
    meta = meta || {};
    var COPY = C.COPY;
    var people = (cohort.sessions || []).filter(function (s) { return s.complete !== false && s.score && s.style; }).map(function (s) {
      return { name: s.name || 'Leader', style: s.style, pace: (s.score.natural && s.score.natural.pace) || 50, priority: (s.score.natural && s.score.natural.priority) || 50 };
    });
    var n = people.length;
    var counts = { TRACTOR: 0, BUS: 0, JET: 0, ROCKET: 0 };
    people.forEach(function (p) { counts[p.style]++; });
    function pct(s) { return n ? Math.round((counts[s] / n) * 100) : 0; }

    /* ---- cover ---- */
    var cover = '<section class="ts-page ts-cover">' +
      (meta.cover ? '<img class="ts-cover-img" src="' + meta.cover + '" alt="">' : '') +
      '<div class="ts-cover-band"><div class="ts-kicker">The Leadership Imprint · Team Imprint</div>' +
      '<h1 class="ts-cover-title">' + esc(cohort.company || 'Your team') + '</h1>' +
      '<div class="ts-cover-sub">How your leaders are balanced</div>' +
      '<div class="ts-cover-meta">' + n + ' leaders assessed' + (meta.date ? '  ·  ' + esc(meta.date) : '') + '</div></div></section>';

    /* ---- the map ---- */
    var roster = '<ol class="ts-roster">' + people.map(function (p) {
      return '<li><span class="ts-dot" style="background:' + HEX[p.style] + '"></span><b>' + esc(p.name) + '</b> <span class="ts-rstyle" style="color:' + HEX[p.style] + '">' + LABEL[p.style] + '</span></li>';
    }).join('') + '</ol>';
    var map = '<section class="ts-page"><div class="ts-eyebrow">The map</div><h2 class="ts-h1">Where your team sits</h2>' +
      '<p class="ts-lede">Every leader you assessed, placed by how they lead. Read the clusters and the gaps.</p>' +
      '<div class="ts-grid">' + teamGrid(people) + '</div>' + roster + '</section>';

    /* ---- distribution + balance read ---- */
    var dist = '<div class="ts-dist">' + ORDER.map(function (s) {
      var w = pct(s);
      return '<div class="ts-drow"><span class="ts-dlab" style="color:' + HEX[s] + '">' + LABEL[s] + '</span>' +
        '<span class="ts-dtrack"><span class="ts-dfill" style="width:' + w + '%;background:' + HEX[s] + '"></span></span>' +
        '<span class="ts-dcount">' + counts[s] + ' · ' + w + '%</span></div>';
    }).join('') + '</div>';

    var reads = '';
    ORDER.forEach(function (s) {
      var p = pct(s);
      if (p >= 50) reads += balanceCard(s, 'Heavily ' + LABEL[s], COPY.fleetConsequence[s], 'over');
      else if (p >= 35) reads += balanceCard(s, 'Leans ' + LABEL[s], COPY.fleetConsequence[s], 'over');
    });
    ORDER.forEach(function (s) {
      if (counts[s] === 0) reads += balanceCard(s, 'No ' + LABEL[s], COPY.missingConsequence[s], 'gap');
      else if (pct(s) <= 15 && n >= 6) reads += balanceCard(s, 'Thin on ' + LABEL[s], COPY.missingConsequence[s], 'gap');
    });
    if (!reads) reads = '<div class="ts-card ts-balanced"><h3 class="ts-h3">A balanced team</h3><p class="ts-body">No style dominates and none is missing. That is rarer than it sounds and worth protecting. Your risk is friction between styles rather than a blind spot. See the next page.</p></div>';

    var balance = '<section class="ts-page"><div class="ts-eyebrow">The balance</div><h2 class="ts-h1">What your mix produces</h2>' +
      dist + '<div class="ts-reads">' + reads + '</div></section>';

    /* ---- friction ---- */
    var FR = [['ROCKET', 'TRACTOR', 'rocket_tractor'], ['BUS', 'ROCKET', 'bus_rocket'], ['JET', 'TRACTOR', 'jet_tractor'], ['BUS', 'JET', 'bus_jet']];
    var frCards = FR.filter(function (f) { return counts[f[0]] >= 1 && counts[f[1]] >= 1 && (counts[f[0]] + counts[f[1]]) >= Math.max(2, Math.round(n * 0.25)); })
      .map(function (f) {
        return '<div class="ts-card"><h3 class="ts-h3"><span style="color:' + HEX[f[0]] + '">' + LABEL[f[0]] + '</span> &harr; <span style="color:' + HEX[f[1]] + '">' + LABEL[f[1]] + '</span></h3><p class="ts-body">' + COPY.frictionCopy[f[2]] + '</p></div>';
      }).join('');
    var friction = '<section class="ts-page"><div class="ts-eyebrow">The friction to watch</div><h2 class="ts-h1">Where it will rub</h2>' +
      '<p class="ts-lede">Most team friction is read as a personality clash when it is a style clash. Name it as style and it becomes workable.</p>' +
      (frCards || '<p class="ts-body">No sharp style pairings stand out in this group.</p>') +
      '<div class="ts-move"><div class="ts-eyebrow">The one move</div><p class="ts-body"><strong>Know which voice is missing and go looking for it before decisions get made.</strong> You are not going to rebuild the team. You can make sure the question the missing style would have asked still gets asked. Name the styles out loud together: friction named as style is workable; friction named as personality is not.</p></div></section>';

    return '<div id="ts" class="ts-doc" style="' + (meta.styleVars || '') + '">' + cover + map + balance + friction + '</div>';
  }
  function balanceCard(style, head, body, kind) {
    return '<div class="ts-card ts-' + kind + '" style="--s:' + HEX[style] + '"><h3 class="ts-h3">' + esc(head) + '</h3><p class="ts-body">' + body + '</p></div>';
  }

  root.TLI = root.TLI || {};
  root.TLI.teamspread = { render: render };
})(typeof window !== 'undefined' ? window : this);
